import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FilePlus2, UserPlus, Radar, UploadCloud, ArrowUpRight, Activity, CalendarDays, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { api, fmtUSD, fmtDate, fmtRel } from "@/lib/api";
import { useMe } from "@/lib/me";
import { canAccess, stageLabel, STORY_STAGES, formatLabel } from "@/lib/constants";
import { PageHeader, Metric, Pill, ScopeBadge, EmptyState, SampleTag, SectionTag, StageTag } from "@/components/app/shared";
import TasksWidget from "@/components/google/TasksWidget";

export default function Dashboard() {
  const me = useMe();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data, staleTime: 15_000 });
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Buenos días" : hour < 19 ? "Buenas tardes" : "Buenas noches";
  const today = new Date().toLocaleDateString("es-SV", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const actions = [
    canAccess(me.role, "editStories") && { label: "Nuevo borrador", icon: FilePlus2, to: "/editorial?new=1", id: "new-story" },
    canAccess(me.role, "crm") && { label: "Agregar lead", icon: UserPlus, to: "/crm?new=1", id: "new-lead" },
    canAccess(me.role, "radar") && { label: "Escanear Radar", icon: Radar, to: "/radar?scan=1", id: "scan-radar" },
    canAccess(me.role, "webflow") && { label: "Enviar a Webflow", icon: UploadCloud, to: "/integraciones", id: "push-webflow" },
    { label: "Mi agenda", icon: CalendarDays, to: "/agenda", id: "open-agenda" },
  ].filter(Boolean);

  return (
    <div className="space-y-8">
      <PageHeader
        kicker={`01 · Centro de Mando · ${today}`}
        title={`${greet}, ${(me.name || "").split(" ")[0]}`}
        subtitle="Resumen ejecutivo de la operación editorial, el libro anual y el pipeline comercial."
      />

      <div className="flex flex-wrap gap-2" data-testid="quick-action-bar">
        {actions.map((a) => (
          <Button key={a.id} variant="outline" className="lift gap-2" onClick={() => navigate(a.to)} data-testid={`quick-action-${a.id}`}>
            <a.icon className="h-4 w-4" strokeWidth={1.75} /> {a.label}
          </Button>
        ))}
      </div>

      {isLoading || !data ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <Metric testid="metric-active-drafts" label="Borradores activos" value={data.activeDrafts} hint="Asignadas, en progreso o en revisión" />
            <Metric testid="metric-pending-webflow" label="Pendientes de Webflow" value={data.pendingWebflow} hint="Listas para sincronizar con el CMS" />
            <Metric testid="metric-book" label="Libro anual" value={`${data.book.lockedPages}/${data.book.totalPages}`} hint={`páginas bloqueadas · ${data.book.percent}%`}>
              <Progress value={data.book.percent} className="mt-3 h-1" />
            </Metric>
            <Metric
              testid="metric-pipeline"
              label="Pipeline abierto"
              value={data.pipeline ? fmtUSD(data.pipeline.open) : "—"}
              hint={data.pipeline ? `${data.pipeline.openCount} negocios · ${fmtUSD(data.pipeline.booked)} reservado` : "Visible para Comercial y Superadmin"}
            />
            <Metric testid="metric-radar" label="Alertas Radar hoy" value={data.radarToday} hint={`${data.radarNew} pitches nuevos en total`} />
          </div>

          {(data.latestRadar || []).length > 0 && (
            <section className="rounded-lg border border-border bg-card" data-testid="dashboard-radar-latest">
              <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" strokeWidth={1.75} />
                  <div className="text-sm font-semibold">Últimos pitches del Radar</div>
                  <span className="rounded-full bg-accent px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    Nuevos
                  </span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate("/radar")} data-testid="dashboard-radar-latest-open">
                  Ver todo <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="grid gap-px bg-border md:grid-cols-3">
                {data.latestRadar.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => navigate(`/radar?item=${r.id}`)}
                    className="flex flex-col gap-3 bg-card p-5 text-left transition-colors hover:bg-accent"
                    data-testid="dashboard-radar-latest-card"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <ScopeBadge scope={r.scope} />
                      <span className="font-mono text-[10.5px] text-muted-foreground">{fmtRel(r.createdAt)}</span>
                    </div>
                    <div className="line-clamp-3 text-[14px] font-medium leading-snug">{r.suggestedTitle}</div>
                    <div className="mt-auto flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                      <span className="truncate">{r.sourceName || formatLabel(r.format)}</span>
                      <span className="font-display text-lg leading-none text-foreground">{r.relevanceScore}</span>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}

          <div className="grid gap-6 xl:grid-cols-3">
            <section className="rounded-lg border border-border bg-card xl:col-span-2">
              <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
                <div className="text-sm font-semibold">Flujo editorial</div>
                <Button variant="ghost" size="sm" onClick={() => navigate("/editorial")} data-testid="dashboard-open-editorial">
                  Abrir mesa <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="grid grid-cols-3 divide-x divide-border border-b border-border md:grid-cols-6">
                {STORY_STAGES.map((s) => (
                  <div key={s.id} className="px-4 py-4">
                    <div className="font-display text-4xl leading-none">{data.storiesByStage[s.id] || 0}</div>
                    <div className="mt-1 text-[11px] leading-tight text-muted-foreground">{s.label}</div>
                  </div>
                ))}
              </div>
              <div className="px-5 py-3 kicker">Próximas publicaciones</div>
              {data.upcoming.length === 0 ? (
                <div className="px-5 pb-5 text-sm text-muted-foreground">No hay publicaciones programadas.</div>
              ) : (
                <div className="divide-y divide-border">
                  {data.upcoming.map((s) => (
                    <button key={s.id} onClick={() => navigate(`/editorial/${s.id}`)} className="flex w-full items-center gap-4 px-5 py-3 text-left transition-colors hover:bg-accent" data-testid="dashboard-upcoming-item">
                      <div className="w-14 shrink-0 font-mono text-[11px] uppercase text-muted-foreground">{fmtDate(s.publishDate)}</div>
                      <div className="min-w-0 flex-1 truncate text-sm">{s.title}</div>
                      <SectionTag section={s.section} className="hidden md:inline-flex" />
                      <StageTag status={s.status} label={stageLabel(s.status)} />
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section className="rounded-lg border border-border bg-card">
              <div className="flex items-center gap-2 border-b border-border px-5 py-3.5">
                <Activity className="h-4 w-4" strokeWidth={1.75} />
                <div className="text-sm font-semibold">Actividad reciente</div>
              </div>
              <div className="max-h-[420px] divide-y divide-border overflow-y-auto" data-testid="activity-feed">
                {data.activity.length === 0 && <div className="p-5 text-sm text-muted-foreground">Sin actividad todavía.</div>}
                {data.activity.map((a) => (
                  <div key={a.id} className="px-5 py-3">
                    <div className="text-[13px] leading-snug">{a.label}</div>
                    <div className="mt-1 font-mono text-[10.5px] text-muted-foreground">
                      {a.userName} · {fmtRel(a.ts)}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <div className="grid gap-6 xl:grid-cols-2">
            <TasksWidget title="Mis tareas" />
            <section className="rounded-lg border border-border bg-card">
              <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
                <div className="flex items-center gap-2 text-sm font-semibold"><CalendarDays className="h-4 w-4" strokeWidth={1.75} /> Próximo en la agenda</div>
                <Button variant="ghost" size="sm" onClick={() => navigate("/agenda")} data-testid="dashboard-open-agenda">
                  Abrir agenda <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="divide-y divide-border">
                {data.upcoming.slice(0, 4).map((s) => (
                  <button key={s.id} onClick={() => navigate(`/editorial/${s.id}`)} className="flex w-full items-center gap-4 px-5 py-3 text-left transition-colors hover:bg-accent">
                    <div className="w-14 shrink-0 font-mono text-[11px] uppercase text-muted-foreground">{fmtDate(s.publishDate)}</div>
                    <div className="min-w-0 flex-1 truncate text-sm">{s.title}</div>
                    <Pill tone="lilac" className="hidden md:inline-flex">Publicación</Pill>
                  </button>
                ))}
                {data.upcoming.length === 0 && (
                  <div className="px-5 py-6 text-sm text-muted-foreground">Agrega fechas en Historias o reuniones en CRM para verlas aquí.</div>
                )}
              </div>
            </section>
          </div>

          {canAccess(me.role, "radar") && (
            <section>
              <div className="mb-3 flex items-center justify-between">
                <div className="kicker">Radar · señales con mayor relevancia</div>
                <Button variant="ghost" size="sm" onClick={() => navigate("/radar")} data-testid="dashboard-open-radar">
                  Ver Radar <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
                </Button>
              </div>
              {data.topRadar.length === 0 ? (
                <EmptyState title="Radar en silencio" text="Ejecuta un escaneo para detectar nuevas señales culturales." />
              ) : (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  {data.topRadar.map((r) => (
                    <button key={r.id} onClick={() => navigate(`/radar?item=${r.id}`)} className="lift flex flex-col rounded-lg border border-border bg-card p-4 text-left" data-testid="dashboard-radar-card">
                      <div className="flex items-center justify-between gap-2">
                        <ScopeBadge scope={r.scope} />
                        <span className="font-display text-2xl">{r.relevanceScore}</span>
                      </div>
                      <div className="mt-3 text-[15px] font-medium leading-snug">{r.suggestedTitle}</div>
                      <div className="mt-auto flex items-center gap-2 pt-3 text-[11px] text-muted-foreground">
                        {formatLabel(r.format)} {r.isSample && <SampleTag />}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}
